import React, { useState } from "react";
import PhotoUploader from "@/components/PhotoUploader";
import { X, Star, ArrowUp, ArrowDown, Save, Plus } from "lucide-react";

const MAX_PHOTOS = 10;

/**
 * Drag-free photo manager used in Musa/Agency dashboard. Supports:
 * - Add photos via uploader (up to MAX_PHOTOS total)
 * - Reorder via up/down arrows
 * - Remove per-photo with confirmation
 * - Mark one as hero (cover)
 *
 * Controlled component: parent owns `photos` (array of URLs) and `heroPhoto`
 * (URL) state and passes callbacks to persist the changes.
 */
export default function PhotoManager({ photos = [], heroPhoto = "", onChange, onHeroChange, testid = "photo-manager" }) {
  const [pendingRemove, setPendingRemove] = useState(null);

  const updatePhotos = (next) => onChange && onChange(next);

  const handleUploaded = (urls) => {
    const incoming = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
    const merged = [...photos, ...incoming].slice(0, MAX_PHOTOS);
    updatePhotos(Array.from(new Set(merged)));
  };

  const remove = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    updatePhotos(next);
    if (heroPhoto === photos[idx] && onHeroChange) onHeroChange(next[0] || "");
    setPendingRemove(null);
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= photos.length) return;
    const next = [...photos];
    [next[idx], next[target]] = [next[target], next[idx]];
    updatePhotos(next);
  };

  const setHero = (url) => {
    if (onHeroChange) onHeroChange(url);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline" style={{ color: "var(--brand-primary)" }}>Fotos del perfil</div>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            Hasta {MAX_PHOTOS} fotos. Marca una como portada con la estrella.
          </p>
        </div>
        <span className="text-xs" style={{ color: photos.length >= MAX_PHOTOS ? "var(--brand-primary)" : "var(--text-muted)" }}>
          {photos.length}/{MAX_PHOTOS}
        </span>
      </div>

      {/* Existing photos grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4" data-testid={`${testid}-grid`}>
          {photos.map((url, idx) => {
            const isHero = url === heroPhoto;
            return (
              <div
                key={url + idx}
                className="relative group"
                style={{ aspectRatio: "3/4", background: "var(--bg-elevated)", border: `1px solid ${isHero ? "var(--vip-gold)" : "var(--border-subtle)"}` }}
                data-testid={`${testid}-item-${idx}`}
              >
                <img src={url} alt="" className="w-full h-full object-cover" />

                {/* Position badge */}
                <span className="absolute top-2 left-2 text-[10px] font-bold px-1.5 py-0.5"
                  style={{ background: "rgba(10,10,10,0.8)", color: "var(--text-primary)" }}>
                  {idx + 1}
                </span>

                {/* Hero badge */}
                {isHero && (
                  <span className="absolute top-2 right-2 text-[9px] uppercase font-bold px-1.5 py-0.5"
                    style={{ background: "var(--vip-gold)", color: "var(--bg-base)" }}
                    data-testid={`${testid}-hero-badge-${idx}`}>
                    Portada
                  </span>
                )}

                {/* Hover controls */}
                <div
                  className="absolute inset-0 flex flex-col justify-between p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.8) 0%, rgba(10,10,10,0.4) 50%, rgba(10,10,10,0.8) 100%)" }}
                >
                  <div className="flex gap-1 justify-end">
                    <button
                      onClick={() => setHero(url)}
                      title="Marcar como portada"
                      className="p-1.5"
                      style={{ background: "rgba(0,0,0,0.8)", color: isHero ? "var(--vip-gold)" : "#fff", border: "1px solid rgba(255,255,255,0.2)" }}
                      data-testid={`${testid}-hero-${idx}`}
                    >
                      <Star size={12} fill={isHero ? "var(--vip-gold)" : "transparent"} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => setPendingRemove(idx)}
                      title="Eliminar"
                      className="p-1.5"
                      style={{ background: "rgba(0,0,0,0.8)", color: "#ef6d6d", border: "1px solid #ef6d6d" }}
                      data-testid={`${testid}-remove-${idx}`}
                    >
                      <X size={12} />
                    </button>
                  </div>

                  <div className="flex gap-1 justify-start">
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      title="Mover izquierda"
                      className="p-1.5"
                      style={{ background: "rgba(0,0,0,0.8)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", opacity: idx === 0 ? 0.3 : 1 }}
                      data-testid={`${testid}-up-${idx}`}
                    >
                      <ArrowUp size={12} style={{ transform: "rotate(-90deg)" }} />
                    </button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={idx === photos.length - 1}
                      title="Mover derecha"
                      className="p-1.5"
                      style={{ background: "rgba(0,0,0,0.8)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", opacity: idx === photos.length - 1 ? 0.3 : 1 }}
                      data-testid={`${testid}-down-${idx}`}
                    >
                      <ArrowDown size={12} style={{ transform: "rotate(-90deg)" }} />
                    </button>
                  </div>
                </div>

                {/* Confirm delete */}
                {pendingRemove === idx && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-2"
                    style={{ background: "rgba(10,10,10,0.94)" }}
                    data-testid={`${testid}-confirm-${idx}`}
                  >
                    <p className="text-xs text-center" style={{ color: "var(--text-primary)" }}>¿Eliminar?</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => remove(idx)}
                        className="text-[10px] px-2 py-1"
                        style={{ background: "#ef6d6d", color: "#fff" }}
                        data-testid={`${testid}-confirm-yes-${idx}`}
                      >Sí</button>
                      <button
                        onClick={() => setPendingRemove(null)}
                        className="text-[10px] px-2 py-1"
                        style={{ background: "transparent", color: "var(--text-secondary)", border: "1px solid var(--border-subtle)" }}
                      >No</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Uploader (hidden when at max) */}
      {photos.length < MAX_PHOTOS ? (
        <div className="mt-2">
          <PhotoUploader
            folder="profiles/"
            multiple={true}
            accept="image"
            testid={`${testid}-uploader`}
            onUploaded={handleUploaded}
          />
        </div>
      ) : (
        <p className="text-xs p-3" style={{ color: "var(--vip-gold)", background: "rgba(212,175,55,0.08)", border: "1px dashed var(--vip-gold)" }}>
          Llegaste al máximo de {MAX_PHOTOS} fotos. Elimina alguna para agregar más.
        </p>
      )}
    </div>
  );
}
